import { BaseClientType, EditClientStepProps } from '../../../types';
import { Box, Button, Divider, IconButton, TextField } from '@mui/material';
import { Controller, useFieldArray } from 'react-hook-form';
import { Icon } from '@iconify/react';
import { JSX } from 'react';
import { LegalEntity } from '../../../../common/types/client';
import {
  validationRegExpPatterns,
  validationsErrorMessages,
} from '../../../../common/lib/validations';
import EditClientAddresses from '../../../components/editClientSteps/editClientAddresses';

/**
 * Legal entity address details edit form
 */
const AddressDetailsStep = (props: EditClientStepProps<LegalEntity>): JSX.Element => {
  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    control: props.control,
    name: 'contacts',
  });

  return (
    <Box>
      {contactFields.map((field, index) => (
        <Box key={field.id} className="grid md:grid-cols-2 mb-4 gap-8">
          {index > 0 && <Divider className="col-span-2 pt-4" />}
          <Controller
            name={`contacts.${index}.firstname`}
            control={props.control}
            rules={{
              required: { value: true, message: validationsErrorMessages.required },
              maxLength: { value: 20, message: validationsErrorMessages.maxLength },
              pattern: {
                value: validationRegExpPatterns.namePattern,
                message: validationsErrorMessages.name,
              },
            }}
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                fullWidth
                label="Contact person first name *"
                placeholder="e.g. John"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name={`contacts.${index}.surname`}
            control={props.control}
            rules={{
              required: { value: true, message: validationsErrorMessages.required },
              maxLength: { value: 20, message: validationsErrorMessages.maxLength },
              pattern: {
                value: validationRegExpPatterns.namePattern,
                message: validationsErrorMessages.name,
              },
            }}
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                fullWidth
                label="Contact person last name *"
                placeholder="e.g. Doe"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name={`contacts.${index}.phone`}
            control={props.control}
            rules={{
              required: { value: true, message: validationsErrorMessages.required },
              pattern: {
                value: validationRegExpPatterns.phonePattern,
                message: validationsErrorMessages.phone,
              },
            }}
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                fullWidth
                type="tel"
                label="Phone number *"
                placeholder="e.g. 0831234567"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name={`contacts.${index}.email`}
            control={props.control}
            rules={{
              required: { value: true, message: validationsErrorMessages.required },
              pattern: {
                value: validationRegExpPatterns.emailPattern,
                message: validationsErrorMessages.email,
              },
            }}
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                fullWidth
                type="email"
                label="Email address *"
                placeholder="e.g. email@johndoe.com"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          {index > 0 && (
            <Box className="col-span-2">
              <IconButton onClick={(): void => removeContact(index)}>
                <Icon width={20} icon="ic:outline-delete" />
              </IconButton>
            </Box>
          )}
        </Box>
      ))}

      <Box className="mb-8">
        <Button
          size="small"
          variant="text"
          onClick={(): void =>
            appendContact({
              email: '',
              firstname: '',
              id: 0,
              phone: '',
              surname: '',
              roleplayerID: props.client.roleplayerID,
            })
          }
          startIcon={<Icon icon="ic:twotone-plus" width={24} />}
        >
          Add another
        </Button>
      </Box>

      <EditClientAddresses {...(props as EditClientStepProps<BaseClientType>)} />
    </Box>
  );
};

export default AddressDetailsStep;
